import { defineStore } from 'pinia';
import axios from '../../axios';
import { useAddressStore } from './address';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    accessToken: null,
    refreshToken: null,
    delivery: false,
    profileImage: null,
    fullName: '',
    email: '',
    phoneNumber: '',
    street1: '',
    street2: '',
    suburb: '',
    pincode: '',
    city: '',
  }),

  actions: {
    async login(credentials) {
      try {
        const response = await axios.post('/login/', credentials);
        const { access_token, refresh_token } = response.data;

        this.accessToken = access_token;
        this.refreshToken = refresh_token;

        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);

        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        // Load the profile before resolving so the destination page isn't a guest flash;
        // a failed fetch must NOT strand valid tokens (initializeAuth recovers them).
        try { await this.fetchUserDetails(); } catch (e) { /* tokens valid; details load on next init */ }
        return response.data;
      } catch (error) {
        let errorMessage = 'An unexpected error occurred. Please try again.';
    
        if (error.response && error.response.data) {
          const errors = error.response.data;
    
          const formattedErrors = Object.values(errors)
            .flat()
            .join(' ');
    
          if (formattedErrors) {
            errorMessage = formattedErrors;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
    
        console.error('Login failed:', errorMessage);
        // keep the server's reason code ('no_password', 'needs_staff_link', …) so the
        // sign-in screen can offer the right way in instead of a bare error
        const err = new Error(errorMessage);
        err.code = error.response?.data?.code || (error.response ? 'invalid' : 'network');
        err.data = error.response?.data || {};
        throw err;
      }
    },

    async applyGuestSession(data) {
      // Seamless guest checkout: log in a freshly-created passwordless account.
      this.accessToken = data.access_token;
      this.refreshToken = data.refresh_token;
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      await this.fetchUserDetails();
      return this.user;
    },

    async fetchUserDetails() {
      try {
        if(this.accessToken){
          const response = await axios.get('/details/');
          this.user = response.data;
          localStorage.setItem('user', JSON.stringify(this.user));
          this.initializeProfile();
          return;
        } else{
          console.log("User not logged in");
        }
        
      } catch (error) {
        console.error('Login failed:', error.response?.data || error.message);
        throw error;
      }
    },

    deliveryEligibilityCheck(){
      const addressStore = useAddressStore()
      if(this.user.primary_address){
        const postal_code = this.user.primary_address.postal_code;
        const eligible_postal_code = addressStore.fetchEligiblePostalCodes();
        console.log(eligible_postal_code)
        this.delivery = eligible_postal_code.includes(parseInt(postal_code));
      }
    },

    async handlePasswordChange(payload) {
      try {
        await axios.post('/change-password/', payload);
      } catch (error) {
        console.error('Password Change failed:', error.response?.data || error.message);
        throw error;
      }
    },
    async register(credentials) {
      let response;
      try {
        response = await axios.post('/register-user/', credentials);
      } catch (error) {
        // expected outcomes (account exists / code sent) are answers, not errors
        if (!error.response?.data?.code) console.error('Registration failed:', error.response?.data || error.message);

        // Field messages as strings + the server's reason code ('account_exists',
        // 'claim_code_sent', 'needs_staff_link') and password-rule codes for translation.
        const body = (error.response && error.response.data) || {};
        const out = {};
        Object.entries(body.errors || {}).forEach(([key, messages]) => {
          out[key] = Array.isArray(messages) ? messages.join(' ') : String(messages);
        });
        out.__code = body.code || (error.response ? (body.errors ? 'invalid' : 'unexpected') : 'network');
        out.__body = body;
        throw out;
      }
      // Account created — auto-login is best-effort; a transient login failure must never
      // report the (successful) registration as failed.
      try {
        await this.login({ email: credentials.email, password: credentials.password });
      } catch (e) { /* account exists; the user can sign in on the login screen */ }
      return response.data;
    },

    logout() {
      this.user = null;
      this.accessToken = null;
      this.refreshToken = null;

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      delete axios.defaults.headers.common['Authorization'];
    },

    initializeAuth() {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      let user = null;
      try { user = JSON.parse(localStorage.getItem('user')); } catch (e) { user = null; }

      // Restore a session on valid tokens even if the cached user JSON is missing/corrupt
      // (previously all three were required, so a failed profile-fetch logged the user out
      // with valid tokens sitting orphaned in localStorage).
      if (accessToken && refreshToken) {
        this.accessToken = accessToken;
        this.refreshToken = refreshToken;
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        if (user) this.user = user;
        else this.fetchUserDetails().catch(() => {});
      }
    },
    getBillingAddress(){
      return this.formatAddressSingleLine(this.user?.billing_address || {})
    },

    formatAddressSingleLine({
      street_address1,
      street_address2,
      suburbs,
      city,
      postal_code,
    } = {}) {
      return [
        [street_address1, street_address2].filter(Boolean).join(', '),
        suburbs?.trim(),
        [city, postal_code].filter(Boolean).join(', '),
      ].filter(Boolean).join(', ');
    },
    setProfileImage(imageUrl) {
      this.profileImage = imageUrl;
    },
    setFirstName(name) {
      this.firstName = name;
    },
    setLastName(name) {
      this.lastName = name;
    },
    setEmail(email) {
      this.email = email;
    },
    setPhoneNumber(phone) {
      this.phoneNumber = phone;
    },
    setStreet1(street) {
      this.street1 = street;
    },
    setStreet2(street) {
      this.street2 = street;
    },
    setSuburb(suburb) {
      this.suburb = suburb;
    },
    setPincode(pincode) {
      this.pincode = pincode;
    },
    setState(state) {
      this.state = state;
    },
    async saveProfile() {
        const payload = { 
          profile_picture: this.profileImage,
          full_name:this.fullName, 
        };
        try{
          await axios.post('/details/', payload);
          await this.fetchUserDetails();
        }
        catch (error) {
        console.error('Error saving profile:', error);
        throw error;
      }
    },
    initializeProfile(){  
      if(this.user){
        this.fullName = this.user.full_name ? this.user.full_name : ""
        this.email = this.user.email;
        this.phoneNumber = this.user.mobile_number;
        if(this.user.billing_address){
          this.street1 = this.user.billing_address.street_address1;
          this.street2 = this.user.billing_address.street_address2;
          this.suburbs = this.user.billing_address.suburbs;
          this.pincode = this.user.billing_address.pincode;
          this.city = this.user.billing_address.city;
        }
      } else {
        console.warn('User not found')
      }
    }
  },
});
